import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Loader2, Globe2, Building2, Users, CheckCircle, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TenantManagementTab = ({ credentials, onSuccess }) => {
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', domain: '', admin_email: '' });
  const [isSaving, setIsSaving] = useState(false);

  const getAuthHeader = () => {
    if (!credentials) return {};
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  };

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenants`, { headers: getAuthHeader() });
      if (response.ok) setTenants(await response.json());
    } catch (err) { console.error('Failed to fetch tenants:', err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchTenants(); }, []);

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenants`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newTenant)
      });
      if (response.ok) {
        setShowCreateForm(false);
        setNewTenant({ name: '', slug: '', domain: '', admin_email: '' });
        fetchTenants();
        onSuccess?.('Tenant created successfully!');
      }
    } catch (err) { console.error('Failed to create tenant:', err); }
    finally { setIsSaving(false); }
  };

  const deleteTenant = async (slug) => {
    if (!window.confirm(`Delete tenant "${slug}"? This will remove all their data.`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenants/${slug}`, {
        method: 'DELETE', headers: getAuthHeader()
      });
      if (response.ok) { fetchTenants(); onSuccess?.('Tenant deleted'); }
    } catch (err) { console.error('Failed to delete tenant:', err); }
  };

  const updateTenant = async () => {
    if (!editingTenant) return;
    setIsSaving(true);
    try {
      const { slug, ...updateData } = editingTenant;
      const response = await fetch(`${BACKEND_URL}/api/tenants/${slug}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (response.ok) { setEditingTenant(null); fetchTenants(); onSuccess?.('Tenant updated'); }
    } catch (err) { console.error('Failed to update tenant:', err); }
    finally { setIsSaving(false); }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenant-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /> Tenant Management</h3>
          <p className="text-sm text-muted-foreground mt-1">Create and manage event sites for different organizations</p>
        </div>
        <button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium transition-all hover:-translate-y-0.5" data-testid="create-tenant-btn">
          <Plus className="w-4 h-4" /> New Tenant
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass-card rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-primary">{tenants.length}</div>
          <div className="text-sm text-muted-foreground">Total Tenants</div>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-400">{tenants.filter(t => t.is_active).length}</div>
          <div className="text-sm text-muted-foreground">Active</div>
        </div>
        <div className="glass-card rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-muted-foreground">{tenants.filter(t => !t.is_active).length}</div>
          <div className="text-sm text-muted-foreground">Inactive</div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="glass-card rounded-xl p-6 border border-primary/30">
          <h4 className="font-semibold mb-4">Create New Tenant</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Organization Name *</label>
              <input type="text" value={newTenant.name} onChange={(e) => { setNewTenant(p => ({ ...p, name: e.target.value, slug: generateSlug(e.target.value) })); }}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" placeholder="Acme Events Inc" data-testid="tenant-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL Slug *</label>
              <input type="text" value={newTenant.slug} onChange={(e) => setNewTenant(p => ({ ...p, slug: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none font-mono" placeholder="acme-events" data-testid="tenant-slug-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Custom Domain</label>
              <input type="text" value={newTenant.domain} onChange={(e) => setNewTenant(p => ({ ...p, domain: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" placeholder="events.acme.com" data-testid="tenant-domain-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Admin Email</label>
              <input type="email" value={newTenant.admin_email} onChange={(e) => setNewTenant(p => ({ ...p, admin_email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" placeholder="admin@acme.com" data-testid="tenant-email-input" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 glass-card rounded-xl hover:bg-white/10">Cancel</button>
            <button onClick={createTenant} disabled={isSaving || !newTenant.name || !newTenant.slug} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50" data-testid="save-tenant-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Create Tenant
            </button>
          </div>
        </div>
      )}

      {/* Tenants List */}
      {tenants.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h4 className="font-semibold text-lg mb-2">No Tenants Yet</h4>
          <p className="text-muted-foreground mb-4">Create your first tenant to start managing multiple event sites.</p>
          <button onClick={() => setShowCreateForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
            <Plus className="w-4 h-4" /> Create First Tenant
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tenants.map(tenant => (
            <div key={tenant.slug} className="glass-card rounded-xl p-5 flex items-center justify-between" data-testid={`tenant-${tenant.slug}`}>
              {editingTenant?.slug === tenant.slug ? (
                <div className="flex-1 grid md:grid-cols-3 gap-3 mr-4">
                  <input type="text" value={editingTenant.name} onChange={(e) => setEditingTenant(p => ({ ...p, name: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
                  <input type="text" value={editingTenant.domain || ''} onChange={(e) => setEditingTenant(p => ({ ...p, domain: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-background border border-border text-sm" placeholder="Custom domain" />
                  <input type="email" value={editingTenant.admin_email || ''} onChange={(e) => setEditingTenant(p => ({ ...p, admin_email: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-background border border-border text-sm" placeholder="Admin email" />
                </div>
              ) : (
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Globe2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{tenant.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-mono">/{tenant.slug}</span>
                        {tenant.domain && <><span>|</span><span>{tenant.domain}</span></>}
                        {tenant.is_active ? (
                          <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-3 h-3" /> Active</span>
                        ) : (
                          <span className="text-muted-foreground">Inactive</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                {editingTenant?.slug === tenant.slug ? (
                  <>
                    <button onClick={updateTenant} disabled={isSaving} className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"><Save className="w-4 h-4" /></button>
                    <button onClick={() => setEditingTenant(null)} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditingTenant({ ...tenant })} className="p-2 hover:bg-white/10 rounded-lg" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteTenant(tenant.slug)} className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantManagementTab;
